import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppliedPromo } from '../../types/common';
import { getCsrfToken } from '../../utils/helpers';
import { ProductCartItemWithSecondPromo } from './useCartPricing';

type UseCartDiscountsArgs = {
    authUser: any;
    cartProducts: ProductCartItemWithSecondPromo[];
    originalTotal: number;
    hasSecondPillowPromo: boolean;
};

export function useCartDiscounts(args: UseCartDiscountsArgs) {
    const {
        authUser,
        cartProducts,
        originalTotal,
        // hasSecondPillowPromo
    } = args;
    const [promoCodeInput, setPromoCodeInput] = useState('');
    const [appliedPromo, setAppliedPromo] = useState<AppliedPromo | null>(null);
    const [promoError, setPromoError] = useState('');
    /* -------- скидочные опции -------- */
    const [birthdayEligible, setBirthdayEligible] = useState(false);
    const [activatedPoints, setActivatedPoints] = useState(0);
    const [bonusEligible, setBonusEligible] = useState<number | null>(null);
    const [selectedDiscount, setSelectedDiscount] = useState<'birthday' | 'bonus' | null>(null);

    // // если активна акция на подушки - принудительно снимаем выбранные скидки/промо-код (иначе сервер отклонит)
    // useEffect(() => {
    //     if (!hasSecondPillowPromo) return;
    //     if (selectedDiscount !== null) setSelectedDiscount(null);
    //     if (appliedPromo) setAppliedPromo(null);
    //     if (promoCodeInput) setPromoCodeInput('');
    //     if (promoError) setPromoError('');
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [hasSecondPillowPromo]);

    // если активна акция на подушки - ДР/бонусы снимаем, но промокод оставляем:
    // он теперь может применяться частично только к разрешённым товарам
    // useEffect(() => {
    //     if (!hasSecondPillowPromo) return;
    //     if (selectedDiscount !== null) setSelectedDiscount(null);
    // }, [hasSecondPillowPromo, selectedDiscount]);

    /* -------- эффекты для скидок -------- */
    useEffect(() => {
        if (!authUser) {
            setBirthdayEligible(false);
            setActivatedPoints(0);
            return;
        }
        let isMounted = true;
        (async () => {
            try {
                const dr = await fetch(`${process.env.REACT_APP_SERVER_URL}/user/discount-eligibility`, { credentials: 'include' });
                if (isMounted && dr.ok) {
                    const { birthdayEligible } = await dr.json();
                    setBirthdayEligible(birthdayEligible);
                }
                const bp = await fetch(`${process.env.REACT_APP_SERVER_URL}/user/bonus-balance`, { credentials: 'include' });
                if (isMounted && bp.ok) {
                    const { activatedPoints } = await bp.json();
                    setActivatedPoints(activatedPoints);
                }
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [authUser]);
    useEffect(() => {
        if (appliedPromo) setSelectedDiscount(null);
    }, [appliedPromo]);

    const discountEligibleTotal = useMemo(() => {
        const total = cartProducts.reduce((sum, p) => {
            const qty = Number.isFinite(Number(p.quantity)) && Number(p.quantity) > 0 ? Math.floor(Number(p.quantity)) : 1;
            const secondQty = typeof p.secondPillowAppliedQty === 'number' ? Math.max(0, Math.trunc(p.secondPillowAppliedQty)) : 0;
            const baseTotal = +(Number(p.price) * qty).toFixed(2);
            const subtotalBeforePromo = typeof p.totalPrice === 'number' ? p.totalPrice : baseTotal;
            const hasItemLevelDiscount = (p as any).discountedPrice != null || (p as any).specialDiscountedPrice != null;
            if (hasItemLevelDiscount) return sum;
            const hasWholeItemSeasonalDiscount = secondQty === 0 && subtotalBeforePromo < baseTotal - 0.01;
            if (hasWholeItemSeasonalDiscount) return sum;
            const eligibleUnits = Math.max(0, qty - secondQty);
            return sum + Number(p.price) * eligibleUnits;
        }, 0);
        return +total.toFixed(2);
    }, [cartProducts]);
    const birthdayDiscountAvailable = birthdayEligible && discountEligibleTotal > 0;

    const showDiscountsUnavailableNotice = useMemo(() => {
        return discountEligibleTotal <= 0 && (birthdayEligible || activatedPoints > 0);
    }, [discountEligibleTotal, birthdayEligible, activatedPoints]);

    useEffect(() => {
        if (!activatedPoints || discountEligibleTotal <= 0) {
            setBonusEligible(null);
            return;
        }
        const cap = +(discountEligibleTotal * 0.7).toFixed(2);
        const useable = Math.min(activatedPoints, cap);
        setBonusEligible(useable > 0 ? useable : null);
    }, [activatedPoints, discountEligibleTotal]);

    const isEmployee = authUser?.status === 'employee';
    function isPromoEligibleItem(item: ProductCartItemWithSecondPromo): boolean {
        const baseTotal = +(Number(item.price) * Number(item.quantity)).toFixed(2);
        const subtotalBeforePromo = typeof item.totalPrice === 'number' ? item.totalPrice : baseTotal;
        const hasNonPromoDiscount =
            subtotalBeforePromo < baseTotal - 0.01 ||
            (item as any).discountedPrice != null ||
            (item as any).specialDiscountedPrice != null ||
            Number(item.secondPillowDiscountAmount || 0) > 0;
        return !hasNonPromoDiscount;
    }
    const promoEligibleTotal = useMemo(() => {
        const total = cartProducts.reduce((sum, item) => {
            const baseTotal = +(Number(item.price) * Number(item.quantity)).toFixed(2);
            return isPromoEligibleItem(item) ? sum + baseTotal : sum;
        }, 0);
        return +total.toFixed(2);
    }, [cartProducts]);
    const promoDiscountAmount = useMemo(() => {
        if (!appliedPromo || promoEligibleTotal <= 0) return 0;
        if (appliedPromo.discountType === 'percentage') {
            return +((promoEligibleTotal * appliedPromo.discountValue) / 100).toFixed(2);
        }
        return +Math.min(promoEligibleTotal, appliedPromo.discountValue).toFixed(2);
    }, [appliedPromo, promoEligibleTotal]);
    useEffect(() => {
        if (!appliedPromo) return;
        if (promoEligibleTotal > 0) return;
        setAppliedPromo(null);
        setPromoError('Промо-код не применяется к товарам со скидкой');
    }, [appliedPromo, promoEligibleTotal]);

    useEffect(() => {
        if (selectedDiscount === 'birthday' && !birthdayDiscountAvailable) {
            setSelectedDiscount(null);
            return;
        }
        if (selectedDiscount === 'bonus' && !(bonusEligible != null && bonusEligible > 0)) {
            setSelectedDiscount(null);
        }
    }, [selectedDiscount, birthdayDiscountAvailable, bonusEligible]);

    // Часть суммы с особыми ценами сотрудника (не участвует в -25% по ДР на сервере)
    const sumSpecialEmployee = useMemo(() => {
        if (!isEmployee) return 0;
        return cartProducts.reduce((s, p) => ((p as any).specialDiscountedPrice != null ? s + (p as any).specialDiscountedPrice * Number(p.quantity) : s), 0);
    }, [isEmployee, cartProducts]);
    // Сумма к оплате за товары (без доставки)
    const goodsPayable = useMemo(() => {
        let v = originalTotal;
        if (selectedDiscount === 'birthday' && birthdayDiscountAvailable) {
            const birthdayDiscountAmount = +((discountEligibleTotal * 25) / 100).toFixed(2);
            v = +Math.max(originalTotal - birthdayDiscountAmount, 0).toFixed(2);
        } else if (selectedDiscount === 'bonus' && bonusEligible !== null) {
            v = +Math.max(originalTotal - bonusEligible, 0).toFixed(2);
        } else if (appliedPromo) {
            v = +Math.max(originalTotal - promoDiscountAmount, 0).toFixed(2);
        }
        return v;
    }, [originalTotal, selectedDiscount, birthdayDiscountAvailable, discountEligibleTotal, bonusEligible, appliedPromo, promoDiscountAmount]);

    const handleApplyPromo = useCallback(async () => {
        if (!promoCodeInput) return;
        if (promoEligibleTotal <= 0) {
            setPromoError('Промо-код не применяется к товарам со скидкой');
            return;
        }
        // доп. защита: если есть акция на подушки - промо не применяем (сервер всё равно отклонит)
        // if (hasSecondPillowPromo) {
        //     setPromoError('Промо-код нельзя применить вместе с акцией на вторую подушку');
        //     return;
        // }
        setPromoError('');
        try {
            const csrf = await getCsrfToken();
            const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/promocodes/validate`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                body: JSON.stringify({ code: promoCodeInput, phone: authUser?.phoneNumber, userID: authUser?._id || authUser?.id || null }),
            });
            if (!res.ok) {
                const { error } = await res.json();
                setPromoError(error || 'Промо-код недействителен');
                return;
            }
            const data = await res.json();
            setAppliedPromo({ code: promoCodeInput, discountType: data.discountType, discountValue: data.discountValue });
        } catch (e) {
            console.error(e);
            setPromoError('Ошибка проверки промо-кода');
        }
    }, [promoCodeInput, promoEligibleTotal, authUser]);

    return {
        promoCodeInput,
        setPromoCodeInput,
        appliedPromo,
        setAppliedPromo,
        promoError,
        setPromoError,
        handleApplyPromo,
        birthdayEligible: birthdayDiscountAvailable,
        activatedPoints,
        bonusEligible,
        selectedDiscount,
        setSelectedDiscount,
        goodsPayable,
        sumSpecialEmployee,
        isEmployee,
        showDiscountsUnavailableNotice,
    };
}
